// components/leaderboard/Leaderboard.tsx

import { useEffect, useState } from 'react';
import { Box, Container, Tab, Tabs, Typography } from '@mui/material';
import { useLeaderboard } from '../../../hooks/useLeaderboardData';
import { TEAMS, type LeaderboardPeriod } from '../../../hooks/useLeaderboardData';
import { TEAM_DATA } from '../../../models/TeamConstants';
import LeaderboardPanel from './LeaderboardPanel';
import LeaderboardPlayerCard from './LeaderboardPlayerCard';
import type { User } from '../../../models/User';
import PageMeta from '../../../components/PageMeta';

interface LeaderboardProps {
  user: User | null;
}

export default function Leaderboard({ user }: LeaderboardProps) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  // Default to weekly period
  const [activePeriod, setActivePeriod] = useState<LeaderboardPeriod>('week');
  const { data: users, isLoading, error } = useLeaderboard(activePeriod);

  useEffect(() => {
    const storedUser = localStorage.getItem('triviaUser');
    if (storedUser) {
      setCurrentUser(JSON.parse(storedUser));
    }
  }, [user]);

  // Filter by team client-side — BE returns top N overall, team tabs slice from that
  const allTime = users ?? [];
  const byTeam = Object.fromEntries(
    TEAMS.map(team => [team, allTime.filter((u: User) => u.team === team)])
  );

  const player = currentUser && users
    ? users.find((u: User) => u.userId === currentUser.userId) ?? null
    : null;

  const currentEntries = activeTab === 0 ? allTime : byTeam[TEAMS[activeTab - 1]];

  const playerRank = player
    ? currentEntries.findIndex((u: User) => u.userId === player.userId) + 1
    : 0;

  if (isLoading) return <Typography>Loading leaderboard...</Typography>;
  if (error) return <Typography color="error">Error loading leaderboard: {error.message}</Typography>;

  return (
    <>
      <PageMeta
        title="Leaderboard | The Quizzler"
        description="See the top Harry Potter trivia players on The Quizzler. Compete weekly, monthly, and all-time across four rival teams."
        canonical="https://thequizzler.app/leaderboard"
      />
      <Container maxWidth="sm">
        {player && playerRank > 0 && (
          <Box mt={2}>
            <LeaderboardPlayerCard
              player={player}
              playerRank={playerRank}
              activePeriod={activePeriod}
            />
          </Box>
        )}

        <Box mt={4}>
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 3 }}
          >
            <Tab label="All" />
            {TEAMS.map(team => (
              <Tab key={team} label={`${TEAM_DATA[team].image} ${team}`} />
            ))}
          </Tabs>

          {activeTab === 0 && (
            <LeaderboardPanel
              title="All"
              entries={allTime}
              highlightUserId={player?.userId}
              activePeriod={activePeriod}
              onPeriodChange={setActivePeriod}
            />
          )}
          {TEAMS.map((team, index) => (
            activeTab === index + 1 && (
              <LeaderboardPanel
                key={team}
                title={`${TEAM_DATA[team].image} ${team}`}
                entries={byTeam[team]}
                highlightUserId={player?.userId}
                activePeriod={activePeriod}
                onPeriodChange={setActivePeriod}
              />
            )
          ))}
        </Box>
      </Container>
    </>
  );
}